import React, { useState } from 'react';
import { useStickyHorizontalScroll } from '../../hooks/useStickyHorizontalScroll';
import { inlineHtmlToMarkdown } from '../../utils/pasteUtils';
import {
  expandBraceVarsInText,
  refreshVariableHtml,
} from '../../utils/variableUtils';
import { CodeBlockBody } from './components/CodeBlockBody';
import { CodeBlockHeader } from './components/CodeBlockHeader';
import { useCodeBlockContent } from './hooks/useCodeBlockContent';
import { useCodeBlockInput } from './hooks/useCodeBlockInput';
import { codeContentRev } from './utils/codeContent';

export { matchLanguage } from './utils/languages';
export {
  canonicalizeCodeHtml,
  isEmptyCodeContent,
  normalizeCodeContent,
} from './utils/codeContent';

/**
 * CodeBlock — monospace snippet with language label, line numbers, copy, and
 * wrap toggle. Content is HTML (like other blocks) so `{{variable}}` inserts
 * interactive pills; Copy writes resolved values.
 *
 * Line breaks are real `\n` characters in text nodes (CSS `white-space: pre` /
 * `pre-wrap` renders them). Legacy `<br>` markup is migrated on load. Plain
 * textarea-era content (no pills / entities) is escaped once so `<` stays literal.
 */
export const CodeBlock = ({
  block,
  onUpdate,
  onDelete,
  resolveVariable,
  availableVariables,
  onCreateVariable,
}) => {
  const language = block.props?.language || 'plaintext';
  const wrapText = block.props?.wrapText !== false; // default ON
  const showLineNumbers = block.props?.showLineNumbers === true; // default OFF

  const [copied, setCopied] = useState(false);

  const {
    content,
    contentRef,
    contentEditableRef,
    commitContent,
    handleChange,
    tryOpenFromBraceKey,
    variablePicker,
  } = useCodeBlockContent({
    block,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const { handleCodePaste, handleKeyDown } = useCodeBlockInput({
    contentEditableRef,
    contentRef,
    commitContent,
    tryOpenFromBraceKey,
    onDelete,
    blockId: block.id,
  });

  // Unwrapped long lines: floating H-scrollbar at the bottom of the scrollport
  const { scrollRef: growScrollRef, bar: stickyHScrollBar } = useStickyHorizontalScroll(
    !wrapText,
    content
  );

  const handleLanguageSelect = (langId) => {
    onUpdate(block.id, { props: { ...block.props, language: langId } });
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    // Emit {{tokens}} then expand — preserves newlines from {{name[*]}}
    // (pill display text alone can collapse whitespace in some hosts).
    const refreshed = refreshVariableHtml(contentRef.current, resolveVariable);
    let text = inlineHtmlToMarkdown(refreshed, { variablesAsValues: false });
    text = expandBraceVarsInText(text, resolveVariable);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable */
    }
  };

  const toggleWrap = (e) => {
    e.stopPropagation();
    onUpdate(block.id, { props: { ...block.props, wrapText: !wrapText } });
  };

  const toggleLineNumbers = (e) => {
    e.stopPropagation();
    onUpdate(block.id, {
      props: { ...block.props, showLineNumbers: !showLineNumbers },
    });
  };

  return (
    <div className="code-block" data-code-rev={codeContentRev(block.content)}>
      <CodeBlockHeader
        language={language}
        wrapText={wrapText}
        showLineNumbers={showLineNumbers}
        copied={copied}
        onLanguageSelect={handleLanguageSelect}
        onToggleWrap={toggleWrap}
        onToggleLineNumbers={toggleLineNumbers}
        onCopy={handleCopy}
      />
      <CodeBlockBody
        blockId={block.id}
        content={content}
        wrapText={wrapText}
        showLineNumbers={showLineNumbers}
        growScrollRef={growScrollRef}
        contentEditableRef={contentEditableRef}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handleCodePaste}
      />
      {stickyHScrollBar}
      {variablePicker}
    </div>
  );
};
